/**
 * CustomerDashboardModern — Feb 2026 (Modern Card Theme · Phase 1)
 *
 * Light, card-based customer dashboard rendered when the user has
 * `ui_theme = 'modern'`. Uses the shared data hooks (React Query for
 * products/orders/favorites/tier, useAuth, useCart) from the legacy
 * dashboard so behavior parity is guaranteed.
 *
 * Structure per approved design blueprint:
 *   1. Welcome hero (emerald wash) + Points card (matte gold)
 *   2. Featured strip (gold ribbons)
 *   3. Category tabs (pill row; active = emerald)
 *   4. Product grid (rounded cards, soft shadows, gold star ratings,
 *      emerald +add button, red heart favorites)
 *
 * Legacy dashboard stays intact — CustomerDashboardShell picks which
 * to render based on `useTheme().isModern`.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Leaf, Cookie, Droplets, Wrench,
  Heart, Plus, Search, Star, Sparkles,
  ShoppingCart, ChevronRight, Package, MapPin,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { PushSubscribeButton } from '../../components/PushSubscribeButton';
import { ModernActiveOrderCard } from '../../components/ModernActiveOrderCard';
import { ReviewLaunchBanner } from '../../components/reviews/ReviewLaunchBanner';
import { useFlag } from '../../context/FeatureFlagsContext';
import { usePoints } from '../../hooks/usePoints';
import { Navbar } from '../../components/Navbar';
import { ProductDetailDrawerModern } from '../../components/ProductDetailDrawerModern';
import { ThemeFeedbackCard } from '../../components/ThemeFeedbackCard';
import { isSoldOut, soldOutLast } from '../../utils/customerStock';
import { SoldOutButton } from '../../components/SoldOutButton';
import { flowerSizesForProduct, effectsForProduct } from '../../utils/flowerSizes';
import { hasVariants, getVariants, variantPriceFor } from '../../utils/productVariants';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// ── helpers ───────────────────────────────────────────────────
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const CATS = [
  { key: 'all',           label: 'All',        Icon: Sparkles },
  { key: 'flower',        label: 'Flower',     Icon: Leaf },
  { key: 'preroll',       label: 'Prerolls',   Icon: Leaf },
  { key: 'edibles',       label: 'Edibles',    Icon: Cookie },
  { key: 'concentrates',  label: 'Concentrates', Icon: Droplets },
  { key: 'merch',         label: 'Merch',      Icon: Wrench },
];

// Compute the lowest customer-visible price for a product ("from $X").
function computeFromPrice(product, region) {
  if (!product) return 0;
  if (product.category === 'flower') {
    const sizes = flowerSizesForProduct(product, region);
    if (sizes.length > 0) return Math.min(...sizes.map((s) => s.price));
  }
  if (hasVariants(product)) {
    const variants = getVariants(product);
    const prices = variants
      .map((v) => variantPriceFor(product, v.key, region))
      .filter((p) => typeof p === 'number' && p > 0);
    if (prices.length > 0) return Math.min(...prices);
  }
  return Number(product.price ?? 0);
}

function StarRating({ rating, count }) {
  const r = Number(rating || 0);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--am-ink-3)' }}>
      <Star size={12} fill="var(--am-gold-500)" stroke="var(--am-gold-500)" />
      <span className="am-tnum" style={{ color: 'var(--am-ink-2)', fontWeight: 500 }}>{r.toFixed(1)}</span>
      {count > 0 && <span>· {count}</span>}
    </span>
  );
}

// ── main ──────────────────────────────────────────────────────
export function CustomerDashboardModern() {
  const { user } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const loyaltyEnabled = useFlag('customer.loyalty', true);
  const favoritesEnabled = useFlag('customer.favorites', true);
  const searchEnabled = useFlag('customer.search', true);
  const tierVisibleEnabled = useFlag('customer.tier_visible', false);

  const { balance: pointsBalance, refresh: refreshPoints } = usePoints();

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [openDetailId, setOpenDetailId] = useState(null);

  const authed = !!(user && user.id);

  // ── data ────────────────────────────────────────────────────
  const productsQuery = useQuery({
    queryKey: ['customer', 'products'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/products`);
      return data || [];
    },
  });
  const ordersQuery = useQuery({
    queryKey: ['customer', 'orders'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/orders`, { withCredentials: true });
      return data || [];
    },
    enabled: authed,
  });
  const favoritesQuery = useQuery({
    queryKey: ['customer', 'favorites'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/users/me/favorites`, { withCredentials: true });
      return data?.favorites || [];
    },
    enabled: authed,
  });
  const tierQuery = useQuery({
    queryKey: ['customer', 'tier'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/users/me/tier`, { withCredentials: true });
      return data || null;
    },
    enabled: authed,
  });

  const products = productsQuery.data || [];
  const orders = ordersQuery.data || [];
  const favorites = favoritesQuery.data || [];
  const tierData = tierQuery.data || null;
  const loading = productsQuery.isLoading;

  // Deep-link ?open={productId}
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('open');
    if (openId) setOpenDetailId(openId);
  }, []);

  // Deep-linked product outside the visible catalog (e.g. out of stock) — fetch it directly.
  const [deepLinkProduct, setDeepLinkProduct] = useState(null);
  useEffect(() => {
    if (!openDetailId || !products.length || products.some((p) => p.id === openDetailId)) return;
    axios.get(`${API_URL}/api/products/${openDetailId}`, { withCredentials: true })
      .then(({ data }) => setDeepLinkProduct(data))
      .catch(() => setDeepLinkProduct(null));
  }, [openDetailId, products]);

  // ── derived ─────────────────────────────────────────────────
  const filteredProducts = products.filter((p) => {
    if (selectedCategory !== 'all' && p.category !== selectedCategory) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const hay = [p.name, p.category, p.strain_type, p.description].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  // Feb 2026 — Modern-theme parity fix (production hotfix).
  // Only the TOP 4 featured items (newest Drop first) live in the hero strip. All other
  // products — including any featured overflow beyond #3 — fall through
  // to the main grid, so every product visible in the Code theme also
  // renders in Modern. Previously the grid used `!p.featured` which
  // silently dropped featured items #4+.
  const topFeatured = filteredProducts
    .filter((p) => p.featured && !isSoldOut(p, user?.region))
    .sort((a, b) => String(b.featured_at || '').localeCompare(String(a.featured_at || '')))
    .slice(0, 4);
  const topFeaturedIds = new Set(topFeatured.map((p) => p.id));
  const featuredProducts = topFeatured;
  const gridProducts = soldOutLast(filteredProducts.filter((p) => !topFeaturedIds.has(p.id)), user?.region);
  const openProduct = openDetailId
    ? products.find((p) => p.id === openDetailId) || (deepLinkProduct?.id === openDetailId ? deepLinkProduct : null)
    : null;
  const activeOrders = orders.filter((o) => !['delivered', 'cancelled'].includes(o.status));
  const outForDelivery = activeOrders.find((o) => o.status === 'out_for_delivery');

  const firstName = (user?.name || 'friend').split(' ')[0];
  const greeting = (() => {
    const hr = new Date().getHours();
    if (hr >= 5 && hr < 12) return 'Good morning';
    if (hr >= 12 && hr < 17) return 'Good afternoon';
    if (hr >= 17 && hr < 22) return 'Good evening';
    return 'Hi';
  })();

  // Tier progress (matte gold card)
  const tierName = tierData?.tier?.name || tierData?.tier_name || 'Member';
  const nextTier = tierData?.next_tier?.name || null;
  const pointsToNext = tierData?.points_to_next != null ? Number(tierData.points_to_next) : null;
  const currentTierPts = tierData?.tier_start_points != null ? Number(tierData.tier_start_points) : 0;
  const nextTierPts = tierData?.next_tier_at != null ? Number(tierData.next_tier_at) : null;
  const progressPct = (nextTierPts && nextTierPts > currentTierPts)
    ? Math.min(100, Math.max(0, ((pointsBalance - currentTierPts) / (nextTierPts - currentTierPts)) * 100))
    : 0;

  // Redeemable dollars from points (20 pts = $1 default)
  const redeemableDollars = Math.floor((pointsBalance || 0) / 20);

  // ── handlers ────────────────────────────────────────────────
  const toggleFavorite = useCallback(async (productId) => {
    if (!authed) return;
    const FAV_KEY = ['customer', 'favorites'];
    queryClient.setQueryData(FAV_KEY, (prev = []) => (
      prev.includes(productId) ? prev.filter((x) => x !== productId) : [...prev, productId]
    ));
    try {
      const { data } = await axios.post(
        `${API_URL}/api/users/me/favorites/${productId}`, {}, { withCredentials: true },
      );
      queryClient.setQueryData(FAV_KEY, data.favorites || []);
    } catch (_) {
      queryClient.setQueryData(FAV_KEY, (prev = []) => (
        prev.includes(productId) ? prev.filter((x) => x !== productId) : [...prev, productId]
      ));
      toast.error('Could not update favorites');
    }
  }, [authed, queryClient]);

  const handleQuickAdd = useCallback((product) => {
    // For flower or variant products, open the drawer so the customer
    // picks a size. For flat-priced products, add directly.
    if (product.category === 'flower' || hasVariants(product)) {
      setOpenDetailId(product.id);
      return;
    }
    addToCart(product, 1);
  }, [addToCart]);

  const handleDrawerAdd = useCallback(({ product, size, quantity, price }) => {
    if (product.category === 'flower' && size) {
      const sizes = flowerSizesForProduct(product, user?.region);
      const cfg = sizes.find((s) => s.key === size);
      if (cfg) {
        addToCart(product, quantity, { size: cfg.key, eighths: cfg.eighths, unitPrice: cfg.price });
      } else {
        addToCart(product, quantity);
      }
    } else if (hasVariants(product) && size) {
      addToCart(product, quantity, { variant: size, unitPrice: price });
    } else {
      addToCart(product, quantity);
    }
  }, [addToCart, user?.region]);

  // ── loading ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="am-modern-scope" style={{ minHeight: '100vh' }} data-testid="customer-dashboard-modern-loading">
        <Navbar />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '70vh', color: 'var(--am-ink-3)' }}>
          Loading…
        </div>
      </div>
    );
  }

  return (
    <div
      className="am-modern-scope"
      style={{ minHeight: '100vh', background: 'var(--am-canvas)' }}
      data-testid="customer-dashboard-modern"
    >
      <Navbar />

      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '20px 16px 96px' }}>

        {/* ── 1. Hero row ────────────────────────────────────── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
            gap: 16,
            marginBottom: 24,
          }}
          className="am-hero-grid"
        >
          {/* Welcome card */}
          <div
            className="am-card-lg"
            style={{ padding: 22, position: 'relative', overflow: 'hidden' }}
            data-testid="modern-welcome-card"
          >
            <div
              aria-hidden
              style={{
                position: 'absolute',
                top: '-60%', right: '-20%',
                width: 260, height: 260,
                background: 'radial-gradient(circle at center, var(--am-emerald-100) 0%, transparent 70%)',
                pointerEvents: 'none',
              }}
            />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div className="am-eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <MapPin size={11} />
                {user?.region ? `Delivering to ${user.region.toUpperCase()}` : 'Ash & Merch'}
              </div>
              <h1
                style={{
                  margin: '8px 0 4px',
                  fontSize: 28, lineHeight: 1.15, fontWeight: 700, letterSpacing: '-0.02em',
                  color: 'var(--am-ink-1)',
                }}
                data-testid="modern-welcome-title"
              >
                {greeting},<br />{firstName}.
              </h1>
              <p style={{ margin: '6px 0 16px', color: 'var(--am-ink-2)', fontSize: 14 }}>
                {outForDelivery
                  ? 'Your order is on the way.'
                  : activeOrders.length > 0
                    ? `You have ${activeOrders.length} order${activeOrders.length > 1 ? 's' : ''} in progress.`
                    : 'What sounds good tonight?'}
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span className="am-chip">
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--am-emerald-500)' }} />
                  Store open
                </span>
                <span className="am-chip">⚡ Avg 35 min</span>
                <PushSubscribeButton variant="modern" testId="modern-push-toggle" />
                {orders.length > 0 && (
                  <button
                    type="button"
                    onClick={() => navigate('/customer/orders')}
                    className="am-chip"
                    style={{ background: 'var(--am-emerald-50)', color: 'var(--am-emerald-700)', border: 0, cursor: 'pointer' }}
                    data-testid="modern-view-orders"
                  >
                    <Package size={12} /> View orders
                    <ChevronRight size={12} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Points / tier card (matte gold) */}
          {loyaltyEnabled && (
            <div
              className="am-points-card"
              style={{ padding: 22 }}
              data-testid="modern-points-card"
            >
              {tierVisibleEnabled && tierName && (
                <span
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: 'var(--am-gold-500)', color: 'var(--am-ink-inv)',
                    fontSize: 11, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase',
                    padding: '4px 10px', borderRadius: 'var(--am-radius-pill)',
                  }}
                >
                  <Star size={11} fill="currentColor" /> {tierName} tier
                </span>
              )}
              <div
                className="am-tnum"
                style={{ marginTop: 14, fontSize: 36, fontWeight: 700, color: 'var(--am-gold-700)', letterSpacing: '-0.02em' }}
                data-testid="modern-points-balance"
              >
                {(pointsBalance || 0).toLocaleString()}
              </div>
              <div style={{ fontSize: 13, color: 'var(--am-gold-600)', marginTop: 4, fontWeight: 500 }}>
                points balance{redeemableDollars > 0 ? ` · $${redeemableDollars} in rewards` : ''}
              </div>
              {nextTier && nextTierPts != null && pointsToNext != null && (
                <>
                  <div
                    style={{
                      marginTop: 14, height: 6,
                      background: 'rgba(107,83,48,.15)',
                      borderRadius: 'var(--am-radius-pill)', overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${progressPct}%`, height: '100%',
                        background: 'var(--am-gold-500)',
                        borderRadius: 'inherit',
                        transition: 'width .4s ease',
                      }}
                      data-testid="modern-tier-progress"
                    />
                  </div>
                  <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 500, color: 'var(--am-gold-700)' }}>
                    <span>{Math.round(progressPct)}% to {nextTier}</span>
                    <span className="am-tnum">+{pointsToNext.toLocaleString()} pts</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <ModernActiveOrderCard orders={activeOrders} />

        {/* ── 2. Theme feedback poll (one-time, +50 pts) ────── */}
        <ThemeFeedbackCard onSubmitted={() => {
          // Refresh both the points balance (usePoints uses useState, not
          // React Query — needs a direct refresh call) and the tier query
          // so the matte-gold card ticks up from 0 → 50 without a nav.
          refreshPoints();
          queryClient.invalidateQueries({ queryKey: ['customer', 'tier'] });
        }} />

        <ReviewLaunchBanner modern onOpen={(pid) => setOpenDetailId(pid)} />

        {/* ── 3. Featured strip ──────────────────────────────── */}
        {featuredProducts.length > 0 && (
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h2 className="am-section-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="am-gold-ribbon">Featured</span>
                <span>The Drop</span>
              </h2>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(4, featuredProducts.length)}, minmax(0, 1fr))`,
                gap: 14,
              }}
              className="am-featured-grid"
            >
              {featuredProducts.map((product) => {
                const fromPrice = computeFromPrice(product, user?.region);
                return (
                  <button
                    type="button"
                    key={product.id}
                    onClick={() => setOpenDetailId(product.id)}
                    className="am-card am-card-hover"
                    style={{
                      padding: 14, cursor: 'pointer', textAlign: 'left', position: 'relative',
                      display: 'flex', flexDirection: 'column', gap: 10, border: 0, background: 'var(--am-card)',
                    }}
                    data-testid={`modern-featured-${product.id}`}
                  >
                    <div
                      className="am-image-scaffold"
                      style={{
                        position: 'relative', width: '100%', height: 0, paddingBottom: '100%', flexShrink: 0,
                        borderRadius: 'var(--am-radius-sm)',
                        overflow: 'hidden',
                        backgroundImage: product.image_url ? `url("${product.image_url}")` : undefined,
                        backgroundSize: 'cover', backgroundPosition: 'center',
                      }}
                    >
                      <span
                        className="am-gold-ribbon"
                        style={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                      >
                        {product.featured_label || 'Staff pick'}
                      </span>
                      {!product.image_url && (
                        <Leaf size={38} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: 'var(--am-emerald-600)', opacity: 0.55 }} />
                      )}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--am-ink-1)', lineHeight: 1.3 }}>
                      {product.name}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', columnGap: 8, rowGap: 2, fontSize: 12, color: 'var(--am-ink-3)' }}>
                      <span style={{ textTransform: 'capitalize' }}>
                        {product.category}{product.strain_type ? ` · ${product.strain_type}` : ''}
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--am-emerald-700)' }} className="am-tnum">
                        from {money(fromPrice)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── 3. Search + Category tabs ──────────────────────── */}
        {searchEnabled && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <div
            style={{
              flex: '1 1 260px',
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'var(--am-card)', border: '1px solid var(--am-hair)',
              borderRadius: 'var(--am-radius-pill)', padding: '10px 14px',
              boxShadow: 'var(--am-shadow-1)',
            }}
          >
            <Search size={14} style={{ color: 'var(--am-ink-3)' }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products…"
              data-testid="modern-search-input"
              style={{
                flex: 1, border: 0, outline: 'none', background: 'transparent',
                fontSize: 14, color: 'var(--am-ink-1)', fontFamily: 'inherit',
              }}
            />
          </div>
        </div>
        )}

        <div
          className="scrollbar-hide"
          style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6, marginBottom: 16 }}
          data-testid="modern-category-row"
        >
          {CATS.map(({ key, label, Icon }) => {
            const active = selectedCategory === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setSelectedCategory(key)}
                className={`am-cat-pill ${active ? 'am-active' : ''}`}
                data-testid={`modern-cat-${key}`}
              >
                <Icon size={13} />
                <span>{label}</span>
              </button>
            );
          })}
        </div>

        {/* ── 4. Product grid ────────────────────────────────── */}
        {gridProducts.length === 0 ? (
          <div
            className="am-card"
            style={{ padding: 40, textAlign: 'center', color: 'var(--am-ink-3)' }}
            data-testid="modern-grid-empty"
          >
            <Leaf size={32} style={{ color: 'var(--am-hair-strong)', margin: '0 auto 8px' }} />
            <div style={{ fontSize: 14, color: 'var(--am-ink-2)', fontWeight: 500 }}>
              Nothing here yet.
            </div>
            <div style={{ fontSize: 13, color: 'var(--am-ink-3)', marginTop: 4 }}>
              Try a different category or search term.
            </div>
          </div>
        ) : (
          <div
            style={{ display: 'grid', gap: 14 }}
            className="am-product-grid"
            data-testid="modern-grid"
          >
            {gridProducts.map((product) => {
              const fromPrice = computeFromPrice(product, user?.region);
              const rating = Number(product.review_rating_avg || 0);
              const reviewCount = Number(product.review_count || 0);
              const isFav = favorites.includes(product.id);
              const soldOut = isSoldOut(product, user?.region);
              return (
                <div
                  key={product.id}
                  className="am-card am-card-hover"
                  style={{
                    overflow: 'hidden', display: 'flex', flexDirection: 'column',
                    cursor: 'pointer',
                  }}
                  onClick={() => setOpenDetailId(product.id)}
                  data-testid={`modern-product-${product.id}`}
                >
                  <div
                    className="am-image-scaffold"
                    style={{
                      aspectRatio: '1 / 1', position: 'relative',
                      backgroundImage: product.image_url ? `url("${product.image_url}")` : undefined,
                      backgroundSize: 'cover', backgroundPosition: 'center',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {!product.image_url && <Leaf size={44} style={{ color: 'var(--am-emerald-600)', opacity: 0.55 }} />}
                    {soldOut && (
                      <span className="am-chip" style={{ position: 'absolute', bottom: 10, left: 10, background: 'rgba(17,22,20,0.82)', color: '#fff' }} data-testid={`modern-sold-out-label-${product.id}`}>
                        Sold out
                      </span>
                    )}

                    {/* Gold "Featured" badge on grid cards whose product is
                        marked featured but didn't make the top-3 hero strip
                        (or when the strip is hidden due to filtering). Keeps
                        the "premium curated" signal on the item wherever it
                        renders. */}
                    {product.featured && (
                      <span
                        className="am-gold-ribbon"
                        style={{
                          position: 'absolute', top: 10, left: 10,
                          boxShadow: 'var(--am-shadow-gold)',
                        }}
                        data-testid={`modern-grid-featured-badge-${product.id}`}
                      >
                        Featured
                      </span>
                    )}

                    {favoritesEnabled && authed && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(product.id); }}
                        aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
                        data-testid={`modern-fav-${product.id}`}
                        style={{
                          position: 'absolute', top: 10, right: 10,
                          width: 32, height: 32, borderRadius: 999, border: 0,
                          background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(6px)',
                          boxShadow: 'var(--am-shadow-1)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer',
                          color: isFav ? '#E11D48' : 'var(--am-ink-3)',
                        }}
                      >
                        <Heart size={15} fill={isFav ? '#E11D48' : 'none'} />
                      </button>
                    )}
                  </div>
                  <div style={{ padding: '12px 14px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div className="am-eyebrow" style={{ textTransform: 'uppercase' }}>
                      {product.category}{product.strain_type ? ` · ${product.strain_type}` : ''}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--am-ink-1)', lineHeight: 1.3 }}>
                      {product.name}
                    </div>
                    {rating > 0 && <StarRating rating={rating} count={reviewCount} />}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 4 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--am-ink-1)' }} className="am-tnum">
                        <small style={{ fontSize: 10, fontWeight: 500, color: 'var(--am-ink-3)', textTransform: 'uppercase', letterSpacing: '.04em', marginRight: 4 }}>from</small>
                        {money(fromPrice)}
                      </span>
                      {soldOut ? (
                        <SoldOutButton product={product} modern compact testId={`modern-sold-out-btn-${product.id}`} />
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleQuickAdd(product); }}
                          aria-label="Add to cart"
                          data-testid={`modern-add-${product.id}`}
                          className="am-icon-btn"
                        >
                          <Plus size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Cart shortcut — floating quick jump to cart if there are items */}
        <FloatingCartFab />

      </div>

      {/* Drawer */}
      {openProduct && (
        <ProductDetailDrawerModern
          product={openProduct}
          onClose={() => setOpenDetailId(null)}
          onAddToCart={handleDrawerAdd}
          region={user?.region}
          isFavorite={favorites.includes(openProduct.id)}
          onToggleFavorite={favoritesEnabled ? toggleFavorite : null}
        />
      )}

      {/* Responsive grid rules */}
      <style>{`
        @media (min-width: 640px) {
          .am-product-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (min-width: 900px) {
          .am-product-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        @media (min-width: 1180px) {
          .am-product-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
        }
        @media (max-width: 700px) {
          .am-hero-grid { grid-template-columns: 1fr !important; }
          .am-featured-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .am-product-grid  { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
      `}</style>
    </div>
  );
}

// Tiny floating cart button on Modern surface — pulls cart count from context.
function FloatingCartFab() {
  const { cartCount } = useCart();
  const navigate = useNavigate();
  if (!cartCount || cartCount <= 0) return null;
  return (
    <button
      type="button"
      onClick={() => navigate('/customer/cart')}
      className="am-cta"
      data-testid="modern-floating-cart"
      style={{
        position: 'fixed', bottom: 20, right: 20, zIndex: 40,
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '12px 18px', fontSize: 14,
        boxShadow: '0 6px 18px var(--am-emerald-ring)',
      }}
    >
      <ShoppingCart size={16} />
      <span>Cart</span>
      <span
        className="am-tnum"
        style={{
          background: 'rgba(255,255,255,0.22)',
          padding: '2px 8px', borderRadius: 999,
          fontSize: 12, fontWeight: 700,
        }}
      >
        {cartCount}
      </span>
    </button>
  );
}

export default CustomerDashboardModern;
